import React from 'react';
import {
  ArrowsOutCardinal,
  Camera,
  CaretRight,
  CheckCircle,
  Circle,
  Code,
  Compass,
  CrosshairSimple,
  CursorClick,
  DownloadSimple,
  FileMagnifyingGlass,
  FrameCorners,
  HandGrabbing,
  HandPointing,
  Keyboard,
  ListChecks,
  MouseSimple,
  PencilSimpleLine,
  Robot,
  RowsPlusBottom,
  Table,
  Textbox,
  Timer,
  UploadSimple,
  XCircle,
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import { Text, Tooltip, TooltipTrigger } from '@chatsdet/ui';
import { motion, AnimatePresence } from 'framer-motion';
import type { ExecutedPlaybookStep } from '@chatsdet/types';
import type { WorkflowRunStepMetadataDto } from '../hooks/useWorkflowRun';

type StepOutcomeDto = NonNullable<WorkflowRunStepMetadataDto['stepOutcomes']>[number];

type StepPlaybookProps = {
  /** Execution-time playbook snapshot persisted on the run step. */
  playbook: ExecutedPlaybookStep[];
  /** Per-step replay outcomes, joined by `order`/`stepOrder`. */
  outcomes?: StepOutcomeDto[];
  /** Expand by default */
  defaultExpanded?: boolean;
};

/** Compact Phosphor icon per playbook action. */
const ACTION_ICONS: Record<string, Icon> = {
  click: CursorClick,
  fill: PencilSimpleLine,
  fill_form: Textbox,
  select: RowsPlusBottom,
  navigate: Compass,
  wait: Timer,
  scroll: MouseSimple,
  hover: HandPointing,
  upload: UploadSimple,
  verify: ListChecks,
  press_key: Keyboard,
  drag: HandGrabbing,
  resize: FrameCorners,
  capture: CrosshairSimple,
  extract_table: Table,
  python_execute: Code,
  verify_download: DownloadSimple,
  verify_download_content: FileMagnifyingGlass,
  screenshot: Camera,
  agent_loop: Robot,
};

function actionIcon(action: string): Icon {
  return ACTION_ICONS[action] ?? ArrowsOutCardinal;
}

type RowOutcome = 'success' | 'failed' | 'not_reached';
type ResolvedOutcome = { state: RowOutcome; error?: string; executor?: RowExecutor };

function resolveOutcome(outcome: StepOutcomeDto | undefined): ResolvedOutcome {
  if (!outcome) return { state: 'not_reached' };
  const executor = resolveExecutor(outcome);
  if (outcome.success === false) return { state: 'failed', error: outcome.error, executor };
  return { state: 'success', executor };
}

type RowExecutor = 'cached' | 'l2' | 'l2-recovery';

/**
 * Which layer executed this playbook step: deterministic cache replay, the
 * playbook session (gap fill), or a deterministic cache heal repairing a
 * broken cached action.
 */
function resolveExecutor(outcome: StepOutcomeDto): RowExecutor {
  if (outcome.recoveredVia) return 'l2-recovery';
  if (outcome.type === 'gap') return 'l2';
  return 'cached';
}

const EXECUTOR_CHIPS: Record<RowExecutor, { label: string; className: string; tooltip: string }> = {
  cached: {
    label: 'Cached',
    className: 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400',
    tooltip: 'Replayed deterministically from the compiled cache.',
  },
  l2: {
    label: 'Playbook',
    className: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400',
    tooltip: 'Executed by the playbook session (no compiled cache entry for this step).',
  },
  'l2-recovery': {
    label: 'Heal',
    className: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400',
    tooltip: 'The cached action failed; deterministic cache heal repaired and re-executed it.',
  },
};

function OutcomeMarker({ state }: { state: RowOutcome }) {
  if (state === 'success') {
    return (
      <CheckCircle
        size={13}
        weight="fill"
        className="text-success shrink-0"
        aria-label="Step succeeded"
      />
    );
  }
  if (state === 'failed') {
    return (
      <XCircle
        size={13}
        weight="fill"
        className="text-destructive shrink-0"
        aria-label="Step failed"
      />
    );
  }
  return (
    <Circle size={13} className="text-muted-foreground/50 shrink-0" aria-label="Step not reached" />
  );
}

function PlaybookRow({
  step,
  outcome,
  index,
}: {
  step: ExecutedPlaybookStep;
  outcome: ResolvedOutcome;
  index: number;
}) {
  const ActionIcon = actionIcon(step.action);
  const paramsText = step.params ? Object.values(step.params).filter(Boolean).join(', ') : '';

  return (
    <div>
      <motion.div
        initial={{ opacity: 0, x: -8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.15, delay: index * 0.04 }}
        className="flex items-center gap-2 py-1 px-1 -mx-1 rounded"
      >
        <TooltipTrigger delay={200}>
          <span className="inline-flex shrink-0 cursor-default">
            <ActionIcon size={13} className="text-muted-foreground" aria-label={step.action} />
          </span>
          <Tooltip>{step.action}</Tooltip>
        </TooltipTrigger>

        <Text size="xs" className="text-foreground/90 truncate min-w-0" title={step.target}>
          {step.target}
        </Text>

        {paramsText && (
          <Text
            size="xs"
            className="text-muted-foreground truncate min-w-0 font-mono"
            title={paramsText}
          >
            {paramsText}
          </Text>
        )}

        <span className="flex-1" />

        {outcome.executor && (
          <TooltipTrigger delay={200}>
            <span
              className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium cursor-default ${EXECUTOR_CHIPS[outcome.executor].className}`}
            >
              {EXECUTOR_CHIPS[outcome.executor].label}
            </span>
            <Tooltip>{EXECUTOR_CHIPS[outcome.executor].tooltip}</Tooltip>
          </TooltipTrigger>
        )}

        <OutcomeMarker state={outcome.state} />
      </motion.div>

      {/* Inline error preview for failed steps */}
      {outcome.state === 'failed' && outcome.error && (
        <Text size="xs" className="text-destructive ml-6 -mt-0.5 mb-0.5">
          {outcome.error}
        </Text>
      )}
      {step.note && (
        <Text size="xs" className="text-muted-foreground italic ml-6 -mt-0.5 mb-0.5">
          {step.note}
        </Text>
      )}
    </div>
  );
}

/**
 * Expandable list of the deterministic playbook steps that replayed for a run
 * step, with per-step pass/fail markers. Follows the same expandable pattern
 * as StepApiCalls. Only rendered when the step actually ran a playbook
 * (`metadataJson.executedPlaybook` persisted at execution time); pure-agent
 * steps and historical runs render nothing.
 */
export function StepPlaybook({ playbook, outcomes, defaultExpanded = false }: StepPlaybookProps) {
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  // Resolve each row's outcome once via an order-keyed map — a find() per
  // row is O(n·m) and this list re-renders during live runs.
  const resolvedByOrder = React.useMemo(() => {
    const byOrder = new Map((outcomes ?? []).map((o) => [o.stepOrder, o]));
    return new Map(playbook.map((s) => [s.order, resolveOutcome(byOrder.get(s.order))]));
  }, [playbook, outcomes]);

  if (playbook.length === 0) {
    return null;
  }

  const failedCount = playbook.filter(
    (s) => resolvedByOrder.get(s.order)?.state === 'failed'
  ).length;

  return (
    <div className="ml-4 mt-1.5">
      {/* Expandable header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors py-1 px-1.5 -ml-1.5 rounded-md hover:bg-muted/50 group"
      >
        <motion.div animate={{ rotate: expanded ? 90 : 0 }} transition={{ duration: 0.15 }}>
          <CaretRight size={11} className="text-muted-foreground group-hover:text-foreground" />
        </motion.div>
        <Text size="xs" className="text-muted-foreground group-hover:text-foreground">
          {playbook.length} playbook step{playbook.length !== 1 ? 's' : ''}
          {failedCount > 0 ? ` · ${failedCount} failed` : ''}
        </Text>
      </button>

      {/* Step rows */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="mt-1 ml-1 space-y-0 border-l border-border/50 pl-3">
              {playbook.map((step, idx) => (
                <PlaybookRow
                  key={step.order}
                  step={step}
                  outcome={resolvedByOrder.get(step.order) ?? { state: 'not_reached' }}
                  index={idx}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
